import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './AuthContext';
import LandingPage from './pages/LandingPage';
import AskNischalsWidget from './components/AskNischalsWidget';
import './App.css';

// Lazy-load every authenticated route so the landing-page bundle stays light.
// Each chunk is fetched only when the user actually navigates there.
const loadMasterLogin = () => import('./pages/MasterLoginPage');
const loadMasterDashboard = () => import('./pages/MasterDashboard');
const loadLogin = () => import('./pages/EnhancedLoginPage');
const loadSuperAdmin = () => import('./pages/ModernSuperAdminDashboard');
const loadTeacher = () => import('./pages/EnhancedTeacherDashboard');
const loadSchoolView = () => import('./pages/AdminSchoolView');
const loadDistrict = () => import('./pages/DistrictDetailPage');
const loadUserMgmt = () => import('./pages/UserManagement');
const loadGroupAdmin = () => import('./pages/GroupAdminDashboard');

const MasterLoginPage = lazy(loadMasterLogin);
const MasterDashboard = lazy(loadMasterDashboard);
const EnhancedLoginPage = lazy(loadLogin);
const ModernSuperAdminDashboard = lazy(loadSuperAdmin);
const EnhancedTeacherDashboard = lazy(loadTeacher);
const AdminSchoolView = lazy(loadSchoolView);
const DistrictDetailPage = lazy(loadDistrict);
const UserManagement = lazy(loadUserMgmt);
const GroupAdminDashboard = lazy(loadGroupAdmin);

// Eager-warm common chunks while the browser is idle so subsequent navigations
// (e.g. /super-admin → /school/<udise>) never trigger the Suspense fallback.
const prefetchAuthChunks = () => {
  const queue = [loadSuperAdmin, loadSchoolView, loadTeacher, loadDistrict, loadUserMgmt, loadMasterDashboard, loadGroupAdmin];
  const run = () => queue.forEach((load) => { try { load(); } catch { /* ignore */ } });
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 2000 });
  } else {
    setTimeout(run, 1500);
  }
};

const RouteSpinner = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-50">
    <div className="w-10 h-10 border-[3px] border-teal-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

const ProtectedRoute = ({ children, allowedRoles, requireMaster = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={requireMaster ? "/master-login" : "/"} />;
  }

  if (requireMaster && !user.is_master_admin) {
    return <Navigate to="/master-login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  return children;
};

function App() {
  useEffect(() => {
    prefetchAuthChunks();
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<RouteSpinner />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/master-login" element={<MasterLoginPage />} />
            <Route path="/login" element={<EnhancedLoginPage />} />

            {/* Master Admin Routes */}
            <Route
              path="/master-dashboard"
              element={
                <ProtectedRoute requireMaster>
                  <MasterDashboard />
                </ProtectedRoute>
              }
            />

            {/* State Admin Routes */}
            <Route
              path="/super-admin"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <ModernSuperAdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/super-admin/manage"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/super-admin/district/:districtId"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <DistrictDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/school/:udise"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'school_admin', 'teacher', 'group_admin']}>
                  <AdminSchoolView />
                </ProtectedRoute>
              }
            />

            {/* Group Admin (school-chain HQ) */}
            <Route
              path="/group-admin"
              element={
                <ProtectedRoute allowedRoles={['group_admin']}>
                  <GroupAdminDashboard />
                </ProtectedRoute>
              }
            />

            {/* Teacher Routes */}
            <Route
              path="/teacher"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <EnhancedTeacherDashboard />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>
        <AskNischalsWidget />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
